import { memo, useMemo, type FC } from 'react';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Button } from '../../../../Controls/Button/index.tsx';
import Icons from '../../../../Elements/Icons/index.ts';
import { Loader } from '../../../../Elements/Loader/index.tsx';
import type { PageDetails } from '../../types.ts';
import { PageInfo } from '../PageInfo/index.tsx';
import { BulkActionsWrapper, FiltersWrapper, InnerWrapper, PageInfoWrapper, Wrapper } from './styled.ts';
import type { TableInfoProps } from './types.ts';

export const TableInfo: FC<TableInfoProps> = memo(function TableInfo({
  className,
  info,
  total,
  pagination,
  isLoading,
  filters,
  bulkActions,
  showBulkActions,
  filtersLoading,
  sorting,
  dataTestId,
}) {
  const pageInfo = useMemo<PageDetails>(() => info ?? { from: 1, to: total, total }, [info, total]);
  const increasedVerticalPadding = Boolean((filters || bulkActions) && total !== undefined);
  const topBorder = Boolean(filters || showBulkActions);
  const withSorting = Boolean(sorting);
  const pageInfoMobileMlAuto = total !== undefined || (pageInfo && pageInfo.to === pageInfo.total);

  const infoFilters = filters && <FiltersWrapper>{!filtersLoading ? filters : <Loader />}</FiltersWrapper>;
  const infoActions = bulkActions && <BulkActionsWrapper>{bulkActions}</BulkActionsWrapper>;

  const nextPage = useCallbackRef(() => {
    pagination?.setPage((prev) => prev + 1);
  });

  const prevPage = useCallbackRef(() => {
    pagination?.setPage((prev) => prev - 1);
  });

  return (
    <Wrapper className={className} data-testid={dataTestId}>
      {showBulkActions ? infoActions : infoFilters}
      <InnerWrapper $increasedVerticalPadding={increasedVerticalPadding} $topBorder={topBorder} $withSorting={withSorting}>
        {sorting}
        <PageInfoWrapper $mobileMlAuto={pageInfoMobileMlAuto}>
          <PageInfo dataTestId='table-total-info' {...pageInfo} isLoading={isLoading} />
        </PageInfoWrapper>
        {pagination && (pageInfo.total > pagination.perPage || isLoading) && (
          <>
            <Button secondary iconOnly onClick={prevPage} disabled={pagination.page === 0} data-testid='backward-button'>
              <Icons.Arrow align='left' />
            </Button>
            <Button
              secondary
              iconOnly
              onClick={nextPage}
              disabled={pageInfo.total === 0 || pagination.page === Math.ceil(pageInfo.total / pagination.perPage) - 1}
              data-testid='forward-button'>
              <Icons.Arrow align='right' />
            </Button>
          </>
        )}
      </InnerWrapper>
    </Wrapper>
  );
});
